import { useState } from "react";
import { RedirectButton } from "../../../common/Button/BackButton";
import { useQueryClient } from "@tanstack/react-query";
import { Table } from "antd";
import createArrayFromSize from "src/utils/creatArrayFromSize";
import { RSelect } from "../../../common/form/select";
import useRegularClass from "src/hooks/useRegularClass";
import { useRegularClassScheduleQuery } from "src/services/react-query-apis/get-regular-class-schedules";
import TimeTableCell from "./time-table-cell";

const TimeTableMaster = () => {
  const regularClass = useRegularClass();

  const [selectedClassId, setSelectedClassId] = useState(0);

  const queryClient = useQueryClient();

  const regularClassScheduleQuery = useRegularClassScheduleQuery({
    id: selectedClassId,
    day_week: null,
    rank: null,
    withInvalidated: null,
    month: null,
    queryConfig: {
      enabled: selectedClassId !== 0,
    },
  });

  const handleSelectedClassChange = value => {
    setSelectedClassId(value.value);
    if (value.value !== 0) {
      queryClient.invalidateQueries({
        queryKey: ["regular-class-schedules", { id: value.value }],
      });
    }
  };

  const regularClassOptionList = () => {
    const options = [{ value: 0, label: "クラスの選択" }];

    if (regularClass.list.length > 0) {
      regularClass.list.forEach(val => {
        options.push({
          value: val.id,
          label: val.affiliation_name,
        });
      });
    }

    return options;
  };

  const dayMap = {
    0: "月",
    1: "火",
    2: "水",
    3: "木",
    4: "金",
    5: "土",
    6: "日",
  };

  const renderScheduleCell = (record, index) => {
    const schedule = record[index];

    return (
      <>
        <TimeTableCell
          schedule={schedule}
          day={dayMap[index]}
          regularClassId={selectedClassId}
        />
      </>
    );
  };

  const columns = [
    {
      title: "月",
      dataIndex: "monday",
      key: "monday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 0),
    },
    {
      title: "火",
      dataIndex: "tuesday",
      key: "tuesday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 1),
    },
    {
      title: "水",
      dataIndex: "wednesday",
      key: "wednesday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 2),
    },
    {
      title: "木",
      dataIndex: "thursday",
      key: "thursday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 3),
    },
    {
      title: "金",
      dataIndex: "friday",
      key: "friday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 4),
    },
    {
      title: "土",
      dataIndex: "saturday",
      key: "saturday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 5),
    },
    {
      title: "日",
      dataIndex: "sunday",
      key: "sunday",
      align: "center",
      render: (_, record) => renderScheduleCell(record, 6),
    },
  ];

  return (
    <>
      <div className="row time-table-container">
        <div className="col-xl-8">
          <div className="card time-table">
            <div className="card-header">
              <div className="d-flex justify-content-between">
                <div className="d-flex gap-2" style={{ width: "250px" }}>
                  <label className="affiliation-label">所属</label>
                  <div className="w-75 affiliation-select">
                    <RSelect
                      value={regularClassOptionList().find(
                        val => val.value === selectedClassId
                      )}
                      onChange={handleSelectedClassChange}
                      options={regularClassOptionList()}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Table
                columns={columns}
                dataSource={generateScheduleTableData(
                  regularClassScheduleQuery?.data?.data
                )}
                loading={
                  selectedClassId !== 0 && regularClassScheduleQuery.isPending
                }
                rowClassName={() => "text-center"}
                locale={{
                  emptyText: regularClassScheduleQuery.error
                    ? "データの取得に失敗しました"
                    : "データがありません",
                }}
                pagination={false}
              />
            </div>
            <div className="card-footer">
              <div className="text-end">
                <RedirectButton
                  redirectTo="/admin/master-maintenance/dashboard"
                  className="btn btn-primary mr-5 text-align-center"
                >
                  戻る
                </RedirectButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const generateScheduleTableData = data => {
  const groupedData = {
    月: [],
    火: [],
    水: [],
    木: [],
    金: [],
    土: [],
    日: [],
  };

  data?.forEach(schedule => {
    groupedData[schedule.day_week].push(schedule);
  });

  const longestArray = Object.values(groupedData).reduce((max, current) => {
    return current.length > max.length ? current : max;
  }, []);

  if (longestArray.length === 0) {
    return [];
  }

  const tableData = createArrayFromSize(
    longestArray.length,
    createArrayFromSize(7, {})
  );

  Object.values(groupedData).forEach((schedules, index) => {
    schedules.forEach((schedule, scheduleIndex) => {
      tableData[scheduleIndex][index] = schedule;
    });
  });

  tableData.push(createArrayFromSize(7, {}));

  return tableData;
};

export default TimeTableMaster;
